import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoHideScrollArea } from '@/components/AutoHideScrollArea';
import { Check } from '@/components/Icons/Check';
import { ChevronDown } from '@/components/Icons/ChevronDown';
import { ChevronRightFilled } from '@/components/Icons/ChevronRightFilled';
import { Modal } from '@/components/Modal';
import { useCloseOnBreakpointChange } from '@/hooks/useCloseOnBreakpointChange';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { ModalSize, ModalVariant } from '@/types/components';
import type { RankingSubject } from '@/types/ratings';
import { tv } from '@/utils/tv';

const triggerChip = tv({
  base: 'flex w-fit cursor-pointer items-center gap-2 rounded-3xl px-3 py-2 shadow-card transition-colors',
  variants: {
    isOpen: {
      true: 'bg-background',
      false: 'bg-popover hover:bg-info-surface',
    },
  },
});

const triggerChevron = tv({
  base: 'size-4 text-primary transition-transform duration-200',
  variants: {
    isOpen: {
      true: 'rotate-180',
      false: '',
    },
  },
});

const option = tv({
  base: 'flex shrink-0 cursor-pointer items-center justify-between gap-2 rounded-xl py-3 pr-2 pl-3',
  variants: {
    isSelected: {
      true: 'bg-info-surface',
      false: 'hover:bg-hover',
    },
  },
});

const optionLabel = tv({
  base: 'truncate',
  variants: {
    isSelected: {
      true: 'text-subhead-14 text-primary',
      false: 'text-body-14 text-muted-foreground',
    },
  },
});

interface Props {
  subjects: RankingSubject[];
  value: number | null;
  onChange: (subjectId: number | null) => void;
}

export const SubjectPicker: React.FC<Props> = ({
  subjects,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { sheetRef, swipeProps } = useSwipeToClose(() => setIsOpen(false));

  const selectedSubject = subjects.find((subject) => subject.id === value);
  const sheetTitle = t('overview.ratings.ranking.subjects');

  const handleSelect = (subjectId: number | null) => {
    setIsOpen(false);
    onChange(subjectId);
  };

  useCloseOnBreakpointChange(isOpen, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen || isMobile) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isMobile]);

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="flex h-10 w-full cursor-pointer items-center gap-3 rounded-xl bg-popover px-4 shadow-card"
        >
          <span className="text-subhead-14 flex-1 truncate text-left text-muted-foreground">
            {selectedSubject?.name}
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </button>

        <Modal
          isOpen={isOpen}
          label={sheetTitle}
          variant={ModalVariant.BottomSheet}
          size={ModalSize.Small}
          contentRef={sheetRef}
          onClose={() => setIsOpen(false)}
        >
          <div
            {...swipeProps}
            className="flex shrink-0 cursor-grab flex-col touch-none active:cursor-grabbing"
          >
            <div className="flex justify-center pt-2 pb-1">
              <div className="h-1 w-10 rounded-full bg-drag-handle" />
            </div>
            <div className="flex items-center px-4 pt-3 pb-3">
              <h2 className="text-[24px]/[30px] font-semibold text-foreground">
                {sheetTitle}
              </h2>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overscroll-contain px-4 pb-[calc(24px+env(safe-area-inset-bottom))]">
            {subjects.map((subject) => {
              const isSelected = subject.id === value;

              return (
                <button
                  key={subject.id ?? 'all'}
                  type="button"
                  onClick={() => handleSelect(subject.id)}
                  className="flex shrink-0 cursor-pointer items-center gap-3 rounded-2xl bg-popover py-3 pr-3 pl-4 shadow-card"
                >
                  <span className="text-subhead-14 flex-1 truncate text-left text-foreground">
                    {subject.name}
                  </span>
                  <span className="flex size-6 shrink-0 items-center justify-center">
                    {isSelected && <Check className="size-4 text-primary" />}
                    {!isSelected && (
                      <ChevronRightFilled className="size-4 text-muted-foreground" />
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </Modal>
      </>
    );
  }

  return (
    <div ref={containerRef} className="relative w-fit">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={triggerChip({ isOpen })}
      >
        <span className="text-caption-12 text-primary">
          {selectedSubject?.name}
        </span>
        <ChevronDown className={triggerChevron({ isOpen })} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 z-10 mt-1 w-fit min-w-[235px] rounded-[14px] bg-popover shadow-card">
          <AutoHideScrollArea
            viewportClassName="max-h-[400px] p-2"
            scrollbarClassName="w-[4px] pr-0"
          >
            <div className="flex flex-col gap-1">
              {subjects.map((subject) => {
                const isSelected = subject.id === value;

                return (
                  <button
                    key={subject.id ?? 'all'}
                    type="button"
                    onClick={() => handleSelect(subject.id)}
                    className={option({ isSelected })}
                  >
                    <span className={optionLabel({ isSelected })}>
                      {subject.name}
                    </span>
                    {isSelected && (
                      <Check className="size-4 shrink-0 text-primary" />
                    )}
                  </button>
                );
              })}
            </div>
          </AutoHideScrollArea>
        </div>
      )}
    </div>
  );
};
